import { useGetAccountsByVesselId } from "@/services/queries/accounts";
import { useGetBudgetsByVesselId } from "@/services/queries/budgets";
import { useGetCurrencies } from "@/services/queries/currency";
import { useGetVendors } from "@/services/queries/vendor";
import { useGetVessels } from "@/services/queries/vessel";
import React, { useEffect, useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Step } from "./constants";
import InitialStep from "./initial-step";
import BudgetStep from "./budget-step";
import VendorStep from "./vendor-step";
import CurrencyStep from "./currency-step";
import NoteStep from "./note-step";
import PaymentMethodStep from "./payment-method-step";
import CostCenter from "./cost-center";
import AmountStep from "./amount-step";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Form } from "@/components/ui/form";
import { Account } from "@/lib/types";
import ExchangeRateStep from "./exchange-rate";
import BudgetCategoryStep from "./budget-category-step";
import BudgetSubcategoryStep from "./budget-subcategory-step";
import VendorPaymentDetailsStep, {
  VendorPaymentDetailsFormValues,
} from "../add-vendor-payment-details";

export type ExpenseFormValues = {
  amount?: number;
  reference?: string;
  budgetId?: string;
  budgetCategoryId?: string;
  budgetSubcategoryId?: string;
  currencyId: string;
  accountId?: string;
  transactionDate?: Date;
  files: { id: string; url: string }[] | File[];
  description: string;
  vesselId: string;
  exchangeRate?: number;
  vendor: string | { id: string } | null; // it can be id or name
  vendorPaymentMethods?: {
    iban: string;
    bankName: string;
    swiftCode: string;
    accountHolderName: string;
    currencyId: string;
    bankAddress: string;
    bankAccountNumber: string;
    paymentTerms: string;
    isDefault: boolean;
  }[];
};

const ExpenseForm = ({
  title,
  open,
  setOpen,
  form,
  step,
  setStep,
  defaultValues,
  onSubmit,
  isSubmitting,
  initialVesselId,
  selectedAccount,
  exchangeRate,
  setExchangeRate,
}: {
  title?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  form: UseFormReturn<ExpenseFormValues>;
  defaultValues: Partial<ExpenseFormValues>;
  onSubmit: (values: ExpenseFormValues) => void;
  /** True while a create-expense (or its vendor) request is in flight; disables Save to prevent duplicate submissions. */
  isSubmitting?: boolean;
  initialVesselId?: string;
  selectedAccount?: Account;
  /** Current rate from useConversionRate (owned by the parent so the hook can sync the form field for submit). */
  exchangeRate: number | undefined;
  /** Setter from useConversionRate — called when the user saves a new rate in the drawer step. */
  setExchangeRate: (rate: number | undefined) => void;
}) => {
  const { t } = useTranslation();
  const { data: currenciesData, isPending: isCurrenciesPending } =
    useGetCurrencies();
  const { data: vendorsData, isPending: isVendorsPending } = useGetVendors();
  const { data: vesselsData } = useGetVessels();
  const { data: accountsData, isPending: isAccountsPending } =
    useGetAccountsByVesselId(form.watch("vesselId"));
  const { data: budgetsData } = useGetBudgetsByVesselId(form.watch("vesselId"));

  const currencies = useMemo(() => {
    return currenciesData?.value ?? [];
  }, [currenciesData?.value]);

  const vendors = useMemo(() => {
    return vendorsData?.value ?? [];
  }, [vendorsData?.value]);

  const vessels = useMemo(() => {
    return vesselsData?.value ?? [];
  }, [vesselsData?.value]);

  const accounts = useMemo(() => {
    return accountsData?.value ?? [];
  }, [accountsData?.value]);

  const budgets = useMemo(() => {
    return budgetsData?.value ?? [];
  }, [budgetsData?.value]);

  const getStep = () => {
    switch (step) {
      case Step.INITAL:
        return (
          <InitialStep
            title={title}
            form={form}
            setStep={setStep}
            currencies={currencies}
            vessels={vessels}
            accounts={accounts}
            budgets={budgets}
            vendors={vendors}
            selectedAccount={selectedAccount}
            exchangeRate={exchangeRate}
            isSubmitting={isSubmitting}
          />
        );
      case Step.BUDGET:
        return <BudgetStep form={form} setStep={setStep} />;
      case Step.CATEGORIES:
        return <BudgetCategoryStep form={form} setStep={setStep} />;
      case Step.SUB_CATEGORIES:
        return <BudgetSubcategoryStep form={form} setStep={setStep} />;
      case Step.VENDOR:
        return (
          <VendorStep
            form={form}
            setStep={setStep}
            vendors={vendors}
            isPending={isVendorsPending}
          />
        );
      case Step.CURRENCY:
        return (
          <CurrencyStep
            isCurrenciesPending={isCurrenciesPending}
            currencies={currencies}
            form={form}
            setStep={setStep}
            vessels={vessels}
          />
        );
      case Step.NOTE:
        return <NoteStep form={form} setStep={setStep} />;
      case Step.PAYMENT_METHOD:
        return (
          <PaymentMethodStep
            form={form}
            setStep={setStep}
            accounts={accounts}
            isPending={isAccountsPending}
            currencies={currencies}
          />
        );
      case Step.COST_CENTER:
        return <CostCenter form={form} setStep={setStep} />;
      case Step.AMOUNT:
        return <AmountStep form={form} setStep={setStep} />;
      case Step.EXCHANGE_RATE:
        return (
          <ExchangeRateStep
            setStep={setStep}
            transactionCurrency={currencies.find(
              (c) => c.id === form.watch("currencyId")
            )}
            accountCurrency={currencies.find(
              (c) => c.id === selectedAccount?.currencyId
            )}
            initialRate={exchangeRate}
            onSave={setExchangeRate}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    // Set the cost center to the selected boat
    if (initialVesselId) {
      form.setValue("vesselId", initialVesselId);
    }
  }, [form, initialVesselId]);

  return (
    <Drawer
      modal={false}
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (!open) {
          setStep(Step.INITAL);
          form.reset({
            ...defaultValues,
          });
        }
      }}
    >
      <DrawerContent
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
        data-testid="expense-form-drawer"
      >
        <DrawerHeader className="hidden">
          <DrawerTitle>{title ?? t("title.add_expense")}</DrawerTitle>
        </DrawerHeader>
        <DrawerDescription />
        <Form {...form}>
          <form
            data-testid="expense-form"
            onSubmit={(e) => {
              form.handleSubmit(onSubmit, (errors) => {
                console.warn(errors);
              })(e);
            }}
          >
            {getStep()}
          </form>
        </Form>

        {/* It should be outside of the drawer form caused of its inside form */}
        {step === Step.VENDOR_PAYMENT_DETAILS && (
          <VendorPaymentDetailsStep
            form={form as UseFormReturn<VendorPaymentDetailsFormValues>}
            setStep={setStep}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default ExpenseForm;
